import React, { useEffect, useRef, useState } from 'react';
import { FormContainer } from 'react-hook-form-mui';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { AutocompleteField, FormButtons, TextField } from 'Common/Form';
import { FORM_MODES } from 'Consts/formModes';
import { DEFAULT_TIMEZONE } from 'Consts/timezone';
import { useSelectUserTimezone } from 'User/data';
import { useAppForm } from 'Utils/customHooks';
import type { DateFormat, TimeFormat } from 'Utils/dateTime';
import {
  convertCourseDatesFormat,
  DateFormat as DateFormatEnum,
  TimeFormat as TimeFormatEnum
} from 'Utils/dateTime';

import { useDateTimeConfigFromConfigSlice } from '@hooks/useDateTimeConfigFromConfigSlice';

import { createNewCourseDateEntry, validateAllCourseDates } from '../../dates';

import { CheckboxOptionsSection } from './components/checkbox-section';
import { CourseDateItem } from './components/course-date-item';
import { ErrorDisplay } from './components/error-display';
import { LoadingSkeleton } from './components/loading-skeleton';
import { ParticipantsSection } from './components/participants-section';
import { useCourseDates } from './hooks/use-course-dates';
import { useCourseFormOptions } from './hooks/use-course-form-options';
import {
  getDefaultFormValues,
  mapDatacenterOptions,
  mapOrganizationOptions,
  mapTimezoneOptions
} from './form-options-utils';
import { createCourseFormSchema } from './schema';
import { FormContainer as StyledFormContainer } from './styled';
import type { CourseDateEntry, CourseFormData, CourseFormProps } from './types';
import { validateFormData } from './validation';

const DEFAULT_COURSE_START_TIME = '09:00';
const DEFAULT_COURSE_END_TIME = '16:00';

export const CourseForm = ({
  mode,
  onSubmit,
  onCancel,
  isSubmitSuccess,
  isLoading,
  initialData
}: Readonly<CourseFormProps>) => {
  const { t } = useTranslation();
  const userTimezone = useSelectUserTimezone();
  const { dateFormat, timeFormat } = useDateTimeConfigFromConfigSlice();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [originalFormData, setOriginalFormData] = useState<Partial<CourseFormData> | null>(null);
  const [formValidationErrors, setFormValidationErrors] = useState<Record<string, string>>({});
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const [courseDates, setCourseDates] = useState<CourseDateEntry[]>([]);
  const [previousDateFormat, setPreviousDateFormat] = useState<DateFormat | null>(null);
  const [previousTimeFormat, setPreviousTimeFormat] = useState<TimeFormat | null>(null);
  const submitButtonRef = useRef<HTMLButtonElement | null>(null);

  const { formOptions, isLoadingOptions, optionsError, isBasicDataLoaded } = useCourseFormOptions();

  const canRenderForm =
    isBasicDataLoaded &&
    (mode === FORM_MODES.CREATE || (mode === FORM_MODES.EDIT && !!initialData));
  const formContext = useAppForm<CourseFormData>(createCourseFormSchema(t), {});

  const currentTimezone = formContext.watch('timezone') || DEFAULT_TIMEZONE;

  const {
    courseDateErrors,
    setCourseDateErrors,
    addCourseDate,
    removeCourseDate,
    updateCourseDateRange,
    validateDateRangeOnBlur,
    getCourseDateForDisplay
  } = useCourseDates({
    formContext,
    canRenderForm,
    courseDates,
    setCourseDates,
    defaultStartTime: DEFAULT_COURSE_START_TIME,
    defaultEndTime: DEFAULT_COURSE_END_TIME,
    currentTimezone,
    dateFormat,
    timeFormat
  });

  useEffect(() => {
    if (isBasicDataLoaded && !originalFormData && formOptions) {
      const defaultValues = getDefaultFormValues(formOptions, mode, initialData, userTimezone);

      let courseDatesToUse = initialData?.courseDates ?? defaultValues.courseDates;

      if (mode === FORM_MODES.CREATE && (!courseDatesToUse || courseDatesToUse.length === 0)) {
        const courseTimezone = defaultValues.timezone ?? DEFAULT_TIMEZONE;
        courseDatesToUse = [
          createNewCourseDateEntry('', '', courseTimezone, undefined, {
            defaultStartTime: DEFAULT_COURSE_START_TIME,
            defaultEndTime: DEFAULT_COURSE_END_TIME,
            dateFormat,
            timeFormat
          })
        ];
      }

      if (courseDatesToUse && courseDatesToUse.length > 0) {
        const initialDateFormat = DateFormatEnum.YYYY_MM_DD;
        const initialTimeFormat = TimeFormatEnum.H24;

        if (dateFormat !== initialDateFormat || timeFormat !== initialTimeFormat) {
          courseDatesToUse = convertCourseDatesFormat(
            courseDatesToUse,
            initialDateFormat,
            initialTimeFormat,
            dateFormat,
            timeFormat
          );
        }
      }

      const formValues = {
        ...defaultValues,
        courseDates: courseDatesToUse
      };

      formContext.reset(formValues);
      setCourseDates(courseDatesToUse);
      setOriginalFormData(formValues);
      setFormKey(prev => prev + 1);
    }
  }, [
    isBasicDataLoaded,
    mode,
    initialData,
    formOptions,
    userTimezone,
    formContext,
    originalFormData,
    dateFormat,
    timeFormat
  ]);

  useEffect(() => {
    if (originalFormData?.courseDates && originalFormData.courseDates.length > 0) {
      formContext.setValue('courseDates', originalFormData.courseDates);
      setCourseDates(originalFormData.courseDates);
    }
  }, [originalFormData, formContext]);

  useEffect(() => {
    if (
      mode === FORM_MODES.EDIT &&
      isBasicDataLoaded &&
      formOptions &&
      originalFormData &&
      initialData
    ) {
      const defaultValues = getDefaultFormValues(formOptions, mode, initialData, userTimezone);
      const formValues = {
        ...defaultValues,
        courseDates: defaultValues.courseDates
      };

      if (formValues.daOrganizationId !== originalFormData.daOrganizationId) {
        formContext.reset(formValues);
        setOriginalFormData(formValues);
      }
    }
  }, [
    formOptions,
    mode,
    isBasicDataLoaded,
    originalFormData,
    initialData,
    userTimezone,
    formContext
  ]);

  useEffect(() => {
    if (isSubmitSuccess) {
      setOriginalFormData(null);
      setIsSubmitting(false);
      setHasAttemptedSubmit(false);
      setFormValidationErrors({});
    }
  }, [isSubmitSuccess]);

  useEffect(() => {
    if (previousDateFormat === null && previousTimeFormat === null) {
      setPreviousDateFormat(dateFormat);
      setPreviousTimeFormat(timeFormat);
    }
  }, [dateFormat, timeFormat, previousDateFormat, previousTimeFormat]);

  useEffect(() => {
    if (
      courseDates.length > 0 &&
      previousDateFormat !== null &&
      previousTimeFormat !== null &&
      (previousDateFormat !== dateFormat || previousTimeFormat !== timeFormat)
    ) {
      const convertedDates = convertCourseDatesFormat(
        courseDates,
        previousDateFormat,
        previousTimeFormat,
        dateFormat,
        timeFormat
      );

      setCourseDates(convertedDates);
      formContext.setValue('courseDates', convertedDates);
    }

    setPreviousDateFormat(dateFormat);
    setPreviousTimeFormat(timeFormat);
  }, [dateFormat, timeFormat, courseDates, formContext, previousDateFormat, previousTimeFormat]);

  const handleOnSubmit = (formData: CourseFormData) => {
    setHasAttemptedSubmit(true);
    setFormValidationErrors({});

    const currentCourseDates = courseDates.length > 0 ? courseDates : formData.courseDates || [];

    const courseDateValidationErrors = validateAllCourseDates(
      currentCourseDates,
      formData.timezone,
      dateFormat,
      timeFormat,
      t
    );
    const formValidationErrors = validateFormData(formData, t);

    setFormValidationErrors(formValidationErrors);
    setCourseDateErrors(courseDateValidationErrors);

    const hasCourseDateErrors = Object.keys(courseDateValidationErrors).length > 0;
    const hasFormErrors = Object.keys(formValidationErrors).length > 0;

    if (hasCourseDateErrors || hasFormErrors) {
      return;
    }

    const submissionData: CourseFormData = {
      ...formData,
      courseDates: currentCourseDates
    };

    setIsSubmitting(true);

    try {
      onSubmit(submissionData);
    } catch {
      if (originalFormData) {
        formContext.reset(originalFormData);
      }
      toast.error(t('errors.genericError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRetry = () => {
    window.location.reload();
  };

  const handleFormKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      event.nativeEvent.preventDefault();
      event.nativeEvent.stopPropagation();
      event.nativeEvent.stopImmediatePropagation();
      return false;
    }
  };

  useEffect(() => {
    const handleGlobalKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        const target = event.target as HTMLElement;
        const formContainer = document.querySelector('[data-form-container]');
        if (formContainer?.contains(target)) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          return false;
        }
      }
    };

    document.addEventListener('keydown', handleGlobalKeyDown, true);
    return () => {
      document.removeEventListener('keydown', handleGlobalKeyDown, true);
    };
  }, []);

  const handleSubmitButtonKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  const isEdit = mode === FORM_MODES.EDIT;

  if (optionsError) {
    return <ErrorDisplay error={optionsError} onRetry={handleRetry} />;
  }

  if (isLoadingOptions || !canRenderForm) {
    return <LoadingSkeleton />;
  }

  if (!formOptions || (formOptions.timezones?.length ?? 0) === 0) {
    return (
      <StyledFormContainer>
        <div>{t('courseForm.optionsNotAvailable')}</div>
      </StyledFormContainer>
    );
  }

  const organizationOptions = mapOrganizationOptions(formOptions.organizations);
  const timezoneOptions = mapTimezoneOptions(formOptions.timezones);
  const datacenterOptions = mapDatacenterOptions(formOptions.datacenters);

  return (
    <div
      onKeyDown={handleFormKeyDown}
      onKeyDownCapture={handleFormKeyDown}
      data-form-container
      role="form"
      tabIndex={-1}
    >
      <FormContainer formContext={formContext} key={formKey}>
        <StyledFormContainer>
          <AutocompleteField
            name="daOrganizationId"
            label={t('courseForm.organization')}
            options={organizationOptions}
            required
            fullWidth
            disabled={isEdit}
          />

          <TextField name="title" label={t('courseForm.courseTitle')} isRequired fullWidth />

          <ParticipantsSection
            hasAttemptedSubmit={hasAttemptedSubmit}
            formValidationErrors={formValidationErrors}
            datacenterOptions={datacenterOptions}
          />

          <AutocompleteField
            name="timezone"
            label={t('courseForm.timezone')}
            options={timezoneOptions}
            required
            fullWidth
            slotProps={{
              select: {
                MenuProps: {
                  style: { zIndex: 1600 },
                  PaperProps: {
                    style: {
                      maxHeight: 300,
                      zIndex: 1600
                    }
                  }
                }
              }
            }}
          />

          {courseDates.map((courseDate, index) => (
            <CourseDateItem
              key={courseDate.id}
              courseDate={courseDate}
              index={index}
              totalDates={courseDates.length}
              userTimezone={userTimezone || DEFAULT_TIMEZONE}
              courseTimezone={formContext.watch('timezone') || DEFAULT_TIMEZONE}
              courseDateErrors={courseDateErrors}
              onDateRangeChange={updateCourseDateRange}
              onDateRangeBlur={validateDateRangeOnBlur}
              onRemoveDate={removeCourseDate}
              onAddDate={addCourseDate}
              getCourseDateForDisplay={getCourseDateForDisplay}
            />
          ))}

          <TextField
            name="description"
            label={t('courseForm.description')}
            multiline
            minRows={2}
            maxRows={10}
            fullWidth
          />

          <CheckboxOptionsSection isEdit={isEdit} />

          <FormButtons
            onSubmit={formContext.handleSubmit(handleOnSubmit)}
            onCancel={onCancel}
            submitText={t(isEdit ? 'courseForm.updateCourse' : 'courseForm.createCourse')}
            cancelText={t('courseForm.cancel')}
            isLoading={isSubmitting || (isLoading ?? false)}
            loadingText={t(isEdit ? 'courseForm.updating' : 'courseForm.creating')}
            submitButtonRef={submitButtonRef}
            onSubmitKeyDown={handleSubmitButtonKeyDown}
          />
        </StyledFormContainer>
      </FormContainer>
    </div>
  );
};
